import { create } from "zustand";

import { FinanceCurrency } from "@/features/finance/model/finance-types";
import { EmploymentType } from "@/features/members/model/members-types";
import {
  DateFormatPreference,
  TenantBillingCycle,
  TenantLocale,
  TenantMembershipStatus,
  TenantPlanTier,
  TenantSupportTier,
  Weekday,
  type TenantSettingsInput,
  type TenantSettingsRecord,
} from "@/features/settings/model/general/general-types";

const DEFAULT_SETTINGS: TenantSettingsRecord = {
  general: {
    logoUrl: "",
    legalName: "",
    displayName: "",
    primaryContactEmail: "",
    supportPhone: "",
    headquartersAddress: "",
  },
  regional: {
    defaultLocale: TenantLocale.Fr,
    defaultTimezone: "Africa/Lubumbashi",
    baseCurrency: FinanceCurrency.USD,
    allowedCurrencies: [FinanceCurrency.CDF, FinanceCurrency.USD],
    exchangeRates: {
      [FinanceCurrency.USD]: 2850,
    },
    dateFormat: DateFormatPreference.DdMmYyyy,
  },
  calendar: {
    workWeekStart: Weekday.Monday,
    workingDays: {
      [Weekday.Monday]: true,
      [Weekday.Tuesday]: true,
      [Weekday.Wednesday]: true,
      [Weekday.Thursday]: true,
      [Weekday.Friday]: true,
      [Weekday.Saturday]: false,
      [Weekday.Sunday]: false,
    },
    workdayStart: "08:00",
    workdayEnd: "17:00",
    publicHolidaysNote: "Utiliser le calendrier national pour les jours feries.",
  },
  operational: {
    employeeIdPrefix: "MBR",
    fiscalYearStartMonth: 1,
    approvalSlaHours: 48,
    activityRetentionMonths: 12,
  },
  people: {
    teams: ["Diaconat", "Louange", "Intercession", "Jeunesse"],
    defaultTeam: "Diaconat",
    allowedEmploymentTypes: [EmploymentType.FullTime, EmploymentType.Contractor, EmploymentType.Intern],
    defaultEmploymentType: EmploymentType.FullTime,
    inviteExpiryDays: 7,
  },
  // Transitional membership shape for backward compatibility during subscription extraction.
  membership: {
    planTier: TenantPlanTier.Business,
    membershipStatus: TenantMembershipStatus.Active,
    billingCycle: TenantBillingCycle.Annual,
    seatLimit: 300,
    billingEmail: "finance@eglise-lagrace.org",
    autoRenew: true,
    contractStartDate: "2026-01-01",
    contractEndDate: "2026-12-31",
    accountManager: "Conseil pastoral",
    supportTier: TenantSupportTier.Priority,
  },
  compliance: {
    dataProcessingContact: "confidentialite@eglise-lagrace.org",
    privacyPolicyUrl: "https://eglise-lagrace.org/confidentialite",
    termsUrl: "https://eglise-lagrace.org/conditions",
    incidentContactEmail: "incident@eglise-lagrace.org",
  },
  updatedAt: "2026-03-01T00:00:00.000Z",
  updatedBy: "admin@eglise-lagrace.org",
};

type TenantSettingsState = {
  settings: TenantSettingsRecord;
  getSettings: () => TenantSettingsRecord;
  updateSettings: (patch: TenantSettingsInput, actor: string) => TenantSettingsRecord;
};

export const useTenantSettingsStore = create<TenantSettingsState>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  getSettings: () => get().settings,
  updateSettings: (patch, actor) => {
    const next: TenantSettingsRecord = {
      ...get().settings,
      ...patch,
      updatedAt: new Date().toISOString(),
      updatedBy: actor,
    };

    set({ settings: next });
    return next;
  },
}));
